import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getSupplierCities, addTourProduct, createNewQuote, getTourProductListForCopy, copyTourProduct, getCommonImagesByCity, downloadCommonImagesToProduct, getProductGallery, saveRepresentativeImage, getConsultList, getConsultInfo, getAgencyList } from '../services/apiService';
import DateRangePicker from './DateRangePicker';

const NewQuoteModal = ({ open, onClose, onCreated }) => {
  const [mounted, setMounted] = useState(false);
  const [animOut, setAnimOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // City options
  const [cityOptions, setCityOptions] = useState([]);
  const [cityQuery, setCityQuery] = useState('');
  const [cityCode, setCityCode] = useState('');
  const [cityOpen, setCityOpen] = useState(false);

  // Copy existing product feature
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [productList, setProductList] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Copy existing consult (quote) feature
  const [consultList, setConsultList] = useState([]);
  const [consultSearchQuery, setConsultSearchQuery] = useState('');
  const [selectedConsult, setSelectedConsult] = useState(null);
  const [copyTab, setCopyTab] = useState('product'); // 'product' | 'consult'

  // Form fields
  const [form, setForm] = useState({
    상품명: '',
    상품타입: '309299', // 기타 (하드코딩)
    시작일: '',
    종료일: '',
    요청자명: '',
    요청자휴대폰: '',
    요청자이메일: '',
    견적내용: '',
    성인수: 1,
    소아수: 0,
    유아수: 0,
    판매업체: '',
    판매업체번호: '',
  });

  // Client (판매거래처) picker state
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientError, setClientError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const clientDropdownRef = useRef(null);

  const filteredClients = useMemo(() => {
    const q = (clientSearch || '').toLowerCase().trim();
    if (!q) return clients;
    return clients.filter(c => {
      const name = String(c?.코드명 || c?.거래처명 || '').toLowerCase();
      const email = String(c?.대표이메일 || '').toLowerCase();
      const owner = String(c?.대표자명 || '').toLowerCase();
      const manager = String(c?.담당자명 || '').toLowerCase();
      const code = String(c?.코드 || c?.업체번호2 || c?.거래처번호 || '').toLowerCase();
      return name.includes(q) || email.includes(q) || owner.includes(q) || manager.includes(q) || code.includes(q);
    });
  }, [clients, clientSearch]);

  const ensureClientsLoaded = async () => {
    if (clients.length > 0 || loadingClients) return;
    try {
      setLoadingClients(true);
      setClientError('');
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) throw new Error('업체번호가 없습니다.');
      const list = await getAgencyList({ companyNo });
      setClients(Array.isArray(list) ? list : []);
    } catch (e) {
      console.warn('거래처 목록 조회 실패:', e);
      setClientError('거래처 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingClients(false);
    }
  };

  const selectClient = (c) => {
    const agencyNo = String(c?.코드 ?? c?.업체번호2 ?? c?.거래처번호 ?? '').trim();
    const agencyName = String(c?.코드명 ?? c?.거래처명 ?? '').trim();
    const name = String(c?.담당자명 || c?.대표자명 || c?.거래처명 || '').trim();
    const email = String(c?.대표이메일 || '').trim();
    const phone = String(c?.대표전화 || '').trim();
    setForm((prev) => ({
      ...prev,
      판매업체: agencyName,
      판매업체번호: agencyNo,
      ...(name ? { 요청자명: name } : {}),
      ...(email ? { 요청자이메일: email } : {}),
      ...(phone ? { 요청자휴대폰: phone } : {}),
    }));
    setClientSearch('');
    setShowClientPicker(false);
  };

  const clearClient = () => {
    setForm((prev) => ({
      ...prev,
      판매업체: '',
      판매업체번호: '',
    }));
    setClientSearch('');
    setShowClientPicker(false);
  };

  useEffect(() => {
    if (!showClientPicker) return undefined;
    const onDocMouseDown = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showClientPicker]);

  // Track if using copy mode
  const [useCopyMode, setUseCopyMode] = useState(false);

  // 소요일 계산
  const 소요일 = useMemo(() => {
    if (!form.시작일 || !form.종료일) return 0;
    try {
      const start = new Date(form.시작일);
      const end = new Date(form.종료일);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      return diff > 0 ? diff : 0;
    } catch {
      return 0;
    }
  }, [form.시작일, form.종료일]);

  // Filtered cities with search
  const filteredCities = useMemo(() => {
    const q = (cityQuery || '').trim().toUpperCase();
    if (!q) return cityOptions;
    return cityOptions.filter(opt => 
      String(opt.코드명 || '').toUpperCase().includes(q)
    );
  }, [cityQuery, cityOptions]);

  // Filtered products with search
  const filteredProducts = useMemo(() => {
    const q = (productSearchQuery || '').trim().toUpperCase();
    if (!q) return productList;
    return productList.filter(p => 
      String(p.상품명 || '').toUpperCase().includes(q) ||
      String(p.도시 || '').toUpperCase().includes(q) ||
      String(p.상품종류 || '').toUpperCase().includes(q)
    );
  }, [productSearchQuery, productList]);

  // Filtered consults with search
  const filteredConsults = useMemo(() => {
    const q = (consultSearchQuery || '').trim().toUpperCase();
    if (!q) return consultList;
    return consultList.filter(r =>
      String(r.견적명 || '').toUpperCase().includes(q) ||
      String(r.요청자명 || '').toUpperCase().includes(q) ||
      String(r.담당자 || '').toUpperCase().includes(q)
    );
  }, [consultSearchQuery, consultList]);

  // Load city options when modal opens
  useEffect(() => {
    if (!open) return;
    setAnimOut(false);
    setMounted(true);
    setError('');
    
    (async () => {
      try {
        setLoading(true);
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) throw new Error('토큰 정보가 없습니다.');
        
        const cities = await getSupplierCities({ companyNo }).catch(() => []);
        setCityOptions(Array.isArray(cities) ? cities : []);
      } catch (e) {
        console.error('도시 목록 로드 실패', e);
        setError('도시 목록을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  if (!open) return null;

  const requestClose = () => {
    setAnimOut(true);
    setTimeout(() => {
      onClose && onClose();
      setMounted(false);
      // Reset form
      setForm({
        상품명: '',
        상품타입: '309299',
        시작일: '',
        종료일: '',
        요청자명: '',
        요청자휴대폰: '',
        요청자이메일: '',
        견적내용: '',
        성인수: 1,
        소아수: 0,
        유아수: 0,
        판매업체: '',
        판매업체번호: '',
      });
      setCityCode('');
      setCityQuery('');
      setShowCopyModal(false);
      setProductList([]);
      setProductSearchQuery('');
      setSelectedProduct(null);
      setUseCopyMode(false);
      setConsultList([]);
      setConsultSearchQuery('');
      setShowClientPicker(false);
      setClientSearch('');
      setSelectedConsult(null);
      setCopyTab('product');
      setClients([]);
      setLoadingClients(false);
      setClientError('');
      setClientSearch('');
      setShowClientPicker(false);
    }, 200);
  };

  // Load product list for copying
  const handleOpenCopyModal = async () => {
    try {
      setLoading(true);
      setError('');
      const [products, consults] = await Promise.all([
        getTourProductListForCopy().catch(() => []),
        getConsultList().catch(() => []),
      ]);
      setProductList(products);
      setConsultList(Array.isArray(consults) ? consults : []);
      setShowCopyModal(true);
    } catch (e) {
      console.error('상품 목록 로드 실패:', e);
      setError('상품 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Handle product selection from copy modal - just set selected product, don't copy yet
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setUseCopyMode(true);
    
    // Pre-fill form with product info
    onChange('상품명', product.상품명);
    
    // Close copy modal
    setShowCopyModal(false);
  };

  // Handle consult selection from copy modal - prefill form with consult info
  const handleSelectConsult = async (row) => {
    try {
      setLoading(true);
      setSelectedConsult(row);
      // Fetch detailed consult info
      let info = null;
      try {
        info = await getConsultInfo({ consultNo: row?.상담번호 });
      } catch (e) {
        info = null;
      }

      const safe = (v) => (v == null ? '' : String(v));
      const num = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      const src = info || {};
      // Prefer detailed info, fallback to list row
      setForm((prev) => ({
        ...prev,
        상품명: safe(src.견적명 || row?.견적명 || prev.상품명),
        요청자명: safe(src.요청자명 || row?.요청자명 || prev.요청자명),
        요청자휴대폰: safe(src.요청자휴대폰 || row?.요청자휴대폰 || prev.요청자휴대폰),
        요청자이메일: safe(src.요청자이메일 || row?.요청자이메일 || prev.요청자이메일),
        시작일: safe(src.시작일 || row?.출발일 || prev.시작일),
        종료일: safe(src.종료일 || prev.종료일),
        견적내용: safe(src.견적내용 || prev.견적내용),
        성인수: num(src.성인수 ?? row?.인원수 ?? prev.성인수),
        소아수: num(src.소아수 ?? prev.소아수),
        유아수: num(src.유아수 ?? prev.유아수),
        판매업체: safe(src.판매업체 || src.판매업체명 || row?.판매업체 || prev.판매업체),
        판매업체번호: safe(src.판매업체번호 ?? row?.판매업체번호 ?? prev.판매업체번호),
      }));

      // Close copy modal
      setShowCopyModal(false);
    } catch (e) {
      console.error('견적 정보 적용 실패:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      
      // Validation
      if (!form.상품명.trim()) {
        setError('상품명을 입력하세요.');
        return;
      }
      if (!useCopyMode && !cityCode) {
        setError('도시를 선택하세요.');
        return;
      }
      if (!form.시작일) {
        setError('시작일을 선택하세요.');
        return;
      }
      if (!form.종료일) {
        setError('종료일을 선택하세요.');
        return;
      }
      if (소요일 <= 0) {
        setError('종료일은 시작일 이후여야 합니다.');
        return;
      }
      if (!form.요청자명.trim()) {
        setError('요청자명을 입력하세요.');
        return;
      }
      if (!form.요청자휴대폰.trim()) {
        setError('요청자 휴대폰을 입력하세요.');
        return;
      }
      if (!form.요청자이메일.trim()) {
        setError('요청자 이메일을 입력하세요.');
        return;
      }

      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setError('로그인 정보를 확인할 수 없습니다.');
        return;
      }

      setLoading(true);

      let productNo;
      
      // 1. 상품 등록 (복사 모드일 때는 복사, 아닐 때는 신규 생성)
      if (useCopyMode && selectedProduct) {
// Copy the product with user-entered information
        const copyResp = await copyTourProduct({
          productTypeName: '기타', // Always use '기타' for quotes
          originalProductNo: selectedProduct.상품번호,
          productName: form.상품명.trim(),
          duration: 소요일,
          durationType: '012001', // Fixed: 일
          currencyCode: '058001', // Fixed: KRW
        });

        const copiedRow = Array.isArray(copyResp?.table) ? copyResp.table[0] : null;
        productNo = copiedRow?.상품번호 || copiedRow?.ProductNo;
        
        if (!productNo) {
          throw new Error('상품 복사 결과를 확인할 수 없습니다.');
        }
} else {
const productResp = await addTourProduct({
          supplierNo: companyNo,
          userNo,
          companyNo,
          productName: form.상품명.trim(),
          cityNo: cityCode,
          durationValue: String(소요일),
          durationUnitCode: '012001', // 일
          currencyCode: '058001', // KRW
          productTypeCode: form.상품타입,
        });

        const productRow = Array.isArray(productResp?.table) ? productResp.table[0] : null;
        productNo = productRow?.상품번호 || productRow?.ProductNo;
        
        if (!productNo) {
          throw new Error('상품 생성 결과를 확인할 수 없습니다.');
        }
// 신규 상품 생성 시 도시 공용이미지에서 랜덤 10장을 대표이미지로 등록
        try {
// 1) 도시 공용이미지 조회
          const commonImages = await getCommonImagesByCity({ cityNo: cityCode });
if (Array.isArray(commonImages) && commonImages.length > 0) {
            // 2) 랜덤으로 최대 10장 선택
            const shuffled = [...commonImages].sort(() => Math.random() - 0.5);
            const selectedImages = shuffled.slice(0, Math.min(10, shuffled.length));
            const selectedImageNos = selectedImages.map(img => img.이미지번호 || img.ImageNo).filter(Boolean);
if (selectedImageNos.length > 0) {
              // 3) 공용이미지를 상품 갤러리로 복사
              await downloadCommonImagesToProduct({
                companyNo,
                userNo,
                productNo,
                imageNos: selectedImageNos,
              });
// 4) 상품 갤러리 조회하여 복사된 이미지 번호 확인
              const gallery = await getProductGallery({ companyNo, productNo });
// 5) 복사된 이미지들을 대표이미지로 등록
              if (Array.isArray(gallery) && gallery.length > 0) {
                // 최대 10장까지만 대표이미지로 등록
                const imagesToRegister = gallery.slice(0, Math.min(10, gallery.length));
                
                for (const img of imagesToRegister) {
                  const imgNo = img.이미지번호 || img.ImageNo;
                  if (imgNo) {
                    await saveRepresentativeImage({
                      imageNo: imgNo,
                      productNo,
                      userNo,
                    });
                  }
                }
}
            }
          } else {
}
        } catch (imgErr) {
          // 이미지 등록 실패해도 견적 등록은 계속 진행
          console.warn('[신규 견적] 대표이미지 자동 등록 실패 (무시됨):', imgErr);
        }
      }

      // 2. 견적 등록
const quoteResp = await createNewQuote({
        productNo,
        userNo,
        companyNo,
        form: {
          요청자명: form.요청자명.trim(),
          요청자휴대폰: form.요청자휴대폰.trim(),
          요청자이메일: form.요청자이메일.trim(),
          시작일: form.시작일,
          종료일: form.종료일,
          견적명: form.상품명.trim(),
          견적내용: form.견적내용.trim(),
          성인수: Number(form.성인수) || 0,
          소아수: Number(form.소아수) || 0,
          유아수: Number(form.유아수) || 0,
          판매업체: String(form.판매업체 || '').trim(),
          판매업체번호: String(form.판매업체번호 || '').trim(),
        },
      });
alert('신규 견적이 성공적으로 등록되었습니다.');
      onCreated && onCreated({ productNo: Number(productNo), quoteResp });
      requestClose();
    } catch (e) {
      console.error('[신규 견적] 등록 실패:', e);
      setError(e?.message || '견적 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const onChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${animOut ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div className={`w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-200 ${animOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'} max-h-[95vh] flex flex-col`}>
        {/* Header with gradient */}
        <div className="px-6 pt-5 pb-3 bg-white flex items-center justify-between">
          <div>
            <h3 className="text-[17px] font-bold text-gray-900">신규 견적 등록</h3>
            <p className="text-gray-500 text-sm mt-0.5">새로운 견적을 등록합니다</p>
          </div>
          <button 
            onClick={requestClose} 
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400" 
            aria-label="닫기"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 text-red-700 border-l-4 border-red-500 rounded-r-lg px-4 py-3 text-sm flex items-start gap-3 animate-shake">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* 기존 정보 가져오기 - 최상단 배치 */}
            {!useCopyMode && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-800">기존 정보 가져오기</h4>
                      <p className="text-xs text-gray-600 mt-0.5">기존 상품 또는 기존 견적 정보를 선택해 빠르게 작성할 수 있습니다</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenCopyModal}
                    className="h-9 px-3.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap"
                    disabled={loading}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    기존 정보 가져오기
                  </button>
                </div>
              </div>
            )}

            {/* 복사된 상품 정보 표시 */}
            {useCopyMode && selectedProduct && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-gray-700 ">복사된 상품</div>
                      <div className="text-sm font-bold text-gray-800 mt-1">
                        {selectedProduct.상품명}
                      </div>
                      <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {selectedProduct.도시}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUseCopyMode(false);
                      setSelectedProduct(null);
                      onChange('상품명', '');
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-white/50 rounded-lg transition-colors"
                  >
                    다른 상품 선택
                  </button>
                </div>
              </div>
            )}

            {/* 선택된 기존 견적 정보 표시 */}
            {selectedConsult && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-amber-200/60 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-amber-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a6 6 0 00-6 6v2.586l-.707.707A1 1 0 004 13h12a1 1 0 00.707-1.707L16 10.586V8a6 6 0 00-6-6zm-3 14a3 3 0 006 0H7z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-amber-800 ">적용된 기존 견적</div>
                      <div className="text-sm font-bold text-amber-900 mt-1">
                        {selectedConsult.견적명 || '-'}
                      </div>
                      <div className="text-xs text-amber-800/90 mt-1">
                        요청자: {selectedConsult.요청자명 || '-'}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedConsult(null)}
                    className="px-3 py-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-white/50 rounded-lg transition-colors"
                  >
                    해제
                  </button>
                </div>
              </div>
            )}

            {/* 상품 정보 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
             
                <h4 className="text-base font-semibold text-gray-800">상담(견적) 정보</h4>
              </div>
            
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">상담(견적)명 *</label>
                  <input
                    type="text"
                    value={form.상품명}
                    onChange={(e) => onChange('상품명', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                    placeholder="상품명을 입력하세요"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">도시 {!useCopyMode && '*'}</label>
                  {useCopyMode ? (
                    <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-2.5 py-2 text-sm text-gray-700 font-medium">
                      {selectedProduct?.도시 || '-'}
                    </div>
                  ) : (
                    <div className="relative">
                      <button
                        type="button"
                        className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-left text-sm bg-white pr-10 hover:border-blue-300 transition-colors"
                        onClick={() => setCityOpen((o) => !o)}
                        disabled={loading}
                      >
                        {(() => {
                          const current = cityOptions.find(c => String(c.코드) === String(cityCode));
                          if (current) return <span className="font-medium">{String(current.코드명)}</span>;
                          return <span className="text-gray-400">도시를 선택하세요</span>;
                        })()}
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                          </svg>
                        </span>
                      </button>
                      {cityOpen && (
                        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
                          <div className="p-3 border-b bg-gray-50">
                            <div className="relative">
                              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <input
                                autoFocus
                                type="text"
                                value={cityQuery}
                                onChange={(e) => setCityQuery(e.target.value)}
                                placeholder="도시 검색"
                                className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                              />
                            </div>
                          </div>
                          <ul className="max-h-56 overflow-auto py-1">
                            {filteredCities.length === 0 && (
                              <li className="px-4 py-3 text-sm text-gray-500 text-center">검색 결과가 없습니다.</li>
                            )}
                            {filteredCities.map((opt) => (
                              <li
                                key={String(opt.코드)}
                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                                  String(opt.코드) === String(cityCode) 
                                    ? 'bg-gray-100 text-gray-800 font-medium' 
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => {
                                  setCityCode(String(opt.코드));
                                  setCityOpen(false);
                                }}
                              >
                                {String(opt.코드명)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <DateRangePicker
                  startDate={form.시작일}
                  endDate={form.종료일}
                  onStartDateChange={(date) => onChange('시작일', date)}
                  onEndDateChange={(date) => onChange('종료일', date)}
                  singleCalendar
                  disabled={loading}
                />

                {소요일 > 0 && (
                  <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg flex items-center gap-3">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <span className="text-sm text-gray-600">총 소요일:</span>
                      <span className="ml-2 text-base font-bold text-blue-600">{소요일}일</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 요청자 정보 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
               
                <h4 className="text-base font-semibold text-gray-800">요청자 정보</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">판매거래처</label>
                  <div className="relative" ref={clientDropdownRef}>
                    <button
                      type="button"
                      className={`w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm text-left flex items-center justify-between gap-2 transition-all ${
                        loading
                          ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
                          : 'bg-white hover:border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30'
                      }`}
                      onClick={async () => {
                        if (loading) return;
                        const next = !showClientPicker;
                        setShowClientPicker(next);
                        if (next) {
                          setClientSearch('');
                          await ensureClientsLoaded();
                        }
                      }}
                      disabled={loading}
                    >
                      <span className={`truncate ${form.판매업체 ? 'text-gray-800' : 'text-gray-500'}`}>
                        {form.판매업체
                          ? `${form.판매업체}${form.판매업체번호 ? ` (${form.판매업체번호})` : ''}`
                          : '직판'}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${showClientPicker ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showClientPicker && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                            placeholder="거래처명 / 담당자 / 대표자 / 이메일 검색"
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>
                        <div className="max-h-44 overflow-y-auto text-sm">
                          {loadingClients && (
                            <div className="px-3 py-3 text-center text-gray-500">거래처를 불러오는 중...</div>
                          )}
                          {!loadingClients && clientError && (
                            <div className="px-3 py-3 text-center text-red-600">{clientError}</div>
                          )}
                          {!loadingClients && !clientError && filteredClients.length === 0 && (
                            <div className="px-3 py-3 text-center text-gray-500">
                              {clientSearch ? '검색 결과가 없습니다' : '표시할 거래처가 없습니다'}
                            </div>
                          )}
                          {!loadingClients && !clientError && filteredClients.length > 0 && (
                            filteredClients.slice(0, 80).map((c, idx) => {
                              const agencyNo = String(c?.코드 ?? c?.업체번호2 ?? c?.거래처번호 ?? '').trim();
                              const agencyName = String(c?.코드명 ?? c?.거래처명 ?? '').trim();
                              const selected = String(form.판매업체번호 || '') === agencyNo && agencyNo !== '';
                              return (
                                <div
                                  key={agencyNo || idx}
                                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selected ? 'bg-blue-100' : ''}`}
                                  onClick={() => selectClient(c)}
                                >
                                  <div className={`font-medium truncate ${selected ? 'text-blue-800' : 'text-gray-800'}`}>
                                    {agencyName || '-'}
                                  </div>
                                  <div className={`text-xs mt-0.5 truncate ${selected ? 'text-blue-700' : 'text-gray-500'}`}>
                                    {[agencyNo, c?.담당자명 || c?.대표자명, c?.대표이메일].filter(Boolean).join(' · ')}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                        <div className="border-t border-gray-200">
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 text-center"
                            onClick={clearClient}
                          >
                            직판 (선택 해제)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">요청자명 *</label>
                  <input
                    type="text"
                    value={form.요청자명}
                    onChange={(e) => onChange('요청자명', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                    placeholder="요청자명을 입력하세요"
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-2">요청자 휴대폰 *</label>
                    <input
                      type="tel"
                      value={form.요청자휴대폰}
                      onChange={(e) => onChange('요청자휴대폰', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                      placeholder="010-1234-5678"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-2">요청자 이메일 *</label>
                    <input
                      type="email"
                      value={form.요청자이메일}
                      onChange={(e) => onChange('요청자이메일', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                      placeholder="example@email.com"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 견적 내용 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
              
                <h4 className="text-base font-semibold text-gray-800">견적 내용</h4>
              </div>
              
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-2">상세 내용</label>
                <textarea
                  rows={4}
                  value={form.견적내용}
                  onChange={(e) => onChange('견적내용', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all resize-none"
                  placeholder="견적 내용을 상세히 입력하세요..."
                  disabled={loading}
                />
              </div>
            </div>

            {/* 인원수 */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
              
                <h4 className="text-base font-semibold text-gray-800">인원수</h4>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">성인</label>
                  <input
                    type="number"
                    min="0"
                    value={form.성인수}
                    onChange={(e) => onChange('성인수', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all text-center font-semibold"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">소아</label>
                  <input
                    type="number"
                    min="0"
                    value={form.소아수}
                    onChange={(e) => onChange('소아수', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all text-center font-semibold"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-2">유아</label>
                  <input
                    type="number"
                    min="0"
                    value={form.유아수}
                    onChange={(e) => onChange('유아수', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2.5 h-9 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all text-center font-semibold"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 pb-5 pt-3 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <span className="font-medium">*</span> 필수 입력 항목
          </div>
          <div className="flex gap-3">
            <button 
              className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors" 
              onClick={requestClose} 
              disabled={loading}
            >
              취소
            </button>
            <button 
              className="h-9 px-3.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2" 
              onClick={handleSubmit} 
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  등록 중...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  견적 등록
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {showCopyModal && createPortal(
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCopyModal(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 pt-5 pb-3 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-[17px] font-bold text-gray-900">기존 정보 가져오기</h3>
                  <p className="text-gray-500 text-sm mt-0.5">아래 탭에서 기존 상품 또는 기존 견적을 선택하세요</p>
                </div>
                <button 
                  onClick={() => setShowCopyModal(false)} 
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400" 
                  aria-label="닫기"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  className={`${copyTab==='product' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'} h-9 px-4 rounded-lg text-sm font-semibold transition-colors`}
                  onClick={() => setCopyTab('product')}
                >
                  상품 선택
                </button>
                <button
                  className={`${copyTab==='consult' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'} h-9 px-4 rounded-lg text-sm font-semibold transition-colors`}
                  onClick={() => setCopyTab('consult')}
                >
                  견적 선택
                </button>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50">
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {copyTab === 'product' ? (
                  <input
                    type="text"
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    placeholder="상품명, 도시, 상품종류로 검색..."
                    className="w-full pl-12 pr-4 h-9 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                  />
                ) : (
                  <input
                    type="text"
                    value={consultSearchQuery}
                    onChange={(e) => setConsultSearchQuery(e.target.value)}
                    placeholder="견적명, 요청자명, 담당자로 검색..."
                    className="w-full pl-12 pr-4 h-9 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-amber-200 transition-all"
                  />
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {copyTab === 'product' ? (
                filteredProducts.length === 0 ? (
                  <div className="text-center py-16">
                    <svg className="mx-auto w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-gray-500 font-medium">
                      {productList.length === 0 ? '상품이 없습니다.' : '검색 결과가 없습니다.'}
                    </p>
                  </div>
                ) : (
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[40%]" />
                      <col className="w-[18%]" />
                      <col className="w-[18%]" />
                      <col className="w-[12%]" />
                      <col className="w-[12%]" />
                    </colgroup>
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">상품명</th>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">도시</th>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">상품종류</th>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">타입</th>
                        <th className="h-10 px-3 text-center text-[13px] font-medium text-gray-500 whitespace-nowrap">선택</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredProducts.map((product, idx) => {
                        const productType = product.상품종류 === '기타' ? '견적' : '일반';
                        return (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              <div className="truncate" title={product.상품명 || '-'}>
                                {product.상품명 || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              <div className="truncate" title={product.도시 || '-'}>
                                {product.도시 || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              <div className="truncate" title={product.상품종류 || '-'}>
                                {product.상품종류 || '-'}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap bg-gray-100 text-gray-700">
                                {productType}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button
                                onClick={() => handleSelectProduct(product)}
                                className="h-9 px-3.5 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                                disabled={loading}
                              >
                                선택
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              ) : (
                filteredConsults.length === 0 ? (
                  <div className="text-center py-16">
                    <svg className="mx-auto w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-gray-500 font-medium">
                      {consultList.length === 0 ? '견적이 없습니다.' : '검색 결과가 없습니다.'}
                    </p>
                  </div>
                ) : (
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[40%]" />
                      <col className="w-[18%]" />
                      <col className="w-[18%]" />
                      <col className="w-[12%]" />
                      <col className="w-[12%]" />
                    </colgroup>
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">견적명</th>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">요청자</th>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">등록일</th>
                        <th className="h-10 px-3 text-left text-[13px] font-medium text-gray-500 whitespace-nowrap">출발일</th>
                        <th className="h-10 px-3 text-center text-[13px] font-medium text-gray-500 whitespace-nowrap">적용</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredConsults.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            <div className="truncate" title={row.견적명 || '-'}>{row.견적명 || '-'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div className="truncate" title={row.요청자명 || '-'}>{row.요청자명 || '-'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div className="truncate" title={row.등록일 || '-'}>{row.등록일 || '-'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            <div className="truncate" title={row.출발일 || '-'}>{row.출발일 || '-'}</div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => handleSelectConsult(row)}
                              className="h-9 px-3.5 text-sm font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
                              disabled={loading}
                            >
                              적용
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>

            <div className="px-6 pb-5 pt-3 flex justify-end">
              <button 
                className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors" 
                onClick={() => setShowCopyModal(false)}
                disabled={loading}
              >
                취소
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
};

export default NewQuoteModal;
